<div class="tab-body mat-elevation-z1">
  <div class="section-header">
    <h2>
      <mat-icon>ad_units</mat-icon>
      Manage Ads
    </h2>
    <div class="section-header-actions">
      <button mat-stroked-button type="button" (click)="refreshAds()" [disabled]="loading">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
      <button mat-flat-button color="primary" type="button" (click)="openCreateAdDialog()">
        <mat-icon>add</mat-icon>
        Create Ad
      </button>
    </div>
  </div>

  <p class="ads-hint">
    These ads appear in the slideshow on the bottom-left of the user dashboard. Add an ad by uploading an
    image or video (or pasting an existing URL). Image ads open the provided link when clicked, while video
    ads play in-place and require a thumbnail image.
  </p>

  <div class="ads-search-row" *ngIf="ads.length > 0">
    <mat-form-field class="search-field" appearance="outline">
      <mat-label>Search ads...</mat-label>
      <input matInput [(ngModel)]="adsSearchTerm" placeholder="Search by image or link">
      <button mat-icon-button matSuffix *ngIf="adsSearchTerm" (click)="adsSearchTerm = ''">
        <mat-icon>close</mat-icon>
      </button>
    </mat-form-field>

    <mat-form-field class="ads-type-filter" appearance="outline">
      <mat-label>Filter by type</mat-label>
      <mat-select [(ngModel)]="adsTypeFilter">
        <mat-option value="all">All</mat-option>
        <mat-option value="image">Images</mat-option>
        <mat-option value="video">Videos</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <ng-container *ngIf="getDisplayedAds() as displayedAds">
    <div class="ads-grid" *ngIf="displayedAds.length > 0">
      <div class="ad-card mat-elevation-z1" *ngFor="let ad of displayedAds; trackBy: trackByAdImage">
        <div class="ad-thumb">
          <img [src]="adPreviewImage(ad)" [alt]="isVideoAd(ad) ? 'Video ad thumbnail' : 'Ad image'" loading="lazy">
          <div class="ad-play-overlay" *ngIf="isVideoAd(ad)" aria-hidden="true">
            <mat-icon>play_arrow</mat-icon>
          </div>
        </div>
        <div class="ad-meta">
          <a class="ad-link" *ngIf="!isVideoAd(ad)" [href]="ad.link" target="_blank" rel="noopener" [matTooltip]="ad.link">{{ ad.link }}</a>
          <span class="ad-type-badge" *ngIf="isVideoAd(ad)">
            <mat-icon>movie</mat-icon> Video
          </span>
          <span class="ad-source" *ngIf="!ad.deletable" matTooltip="External media - only the carousel entry is removed">External media</span>
        </div>
        <button mat-stroked-button color="warn" type="button" class="ad-remove-btn delete-btn"
          (click)="removeAd(ad)" [disabled]="isActionBusy(adDeleteActionKey(ad.image))">
          <mat-spinner *ngIf="isActionBusy(adDeleteActionKey(ad.image))" diameter="14" class="btn-spinner"></mat-spinner>
          <mat-icon *ngIf="!isActionBusy(adDeleteActionKey(ad.image))">delete</mat-icon>
          Remove
        </button>
      </div>
    </div>
  </ng-container>

  <div class="loading-overlay" *ngIf="loading">
    <mat-spinner></mat-spinner>
  </div>

  <div class="empty-state" *ngIf="ads.length === 0 && !loading">
    <mat-icon>ad_units</mat-icon>
    <p>No ads configured yet.</p>
  </div>

  <div class="modal-backdrop" *ngIf="adDialogOpen">
    <div class="modal-card mat-elevation-z8 editor-modal-card">
      <div class="modal-header">
        <div>
          <h3 class="editor-modal-title">
            <mat-icon>ad_units</mat-icon>
            Create Ad
          </h3>
          <p class="editor-modal-subtitle">Upload or link media for the dashboard carousel.</p>
        </div>
        <button mat-icon-button type="button" (click)="closeCreateAdDialog()" aria-label="Close ad dialog">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <div class="modal-body editor-modal-body">
        <div class="ad-form ad-form--dialog">
          <div class="ad-toggle-row">
            <div class="ad-mode-toggle">
              <button mat-stroked-button type="button" [color]="adMediaType === 'image' ? 'primary' : undefined"
                [class.active]="adMediaType === 'image'" (click)="setAdMediaType('image')">
                <mat-icon>image</mat-icon>
                Image ad
              </button>
              <button mat-stroked-button type="button" [color]="adMediaType === 'video' ? 'primary' : undefined"
                [class.active]="adMediaType === 'video'" (click)="setAdMediaType('video')">
                <mat-icon>movie</mat-icon>
                Video ad
              </button>
            </div>

            <div class="ad-mode-toggle">
              <button mat-stroked-button type="button" [color]="adMode === 'upload' ? 'primary' : undefined"
                [class.active]="adMode === 'upload'" (click)="setAdMode('upload')">
                <mat-icon>upload</mat-icon>
                Upload {{ adMediaType === 'video' ? 'video' : 'image' }}
              </button>
              <button mat-stroked-button type="button" [color]="adMode === 'url' ? 'primary' : undefined"
                [class.active]="adMode === 'url'" (click)="setAdMode('url')">
                <mat-icon>link</mat-icon>
                Use {{ adMediaType === 'video' ? 'video' : 'image' }} URL
              </button>
            </div>
          </div>

          <div class="ad-form-fields">
            <div class="ad-file-picker" *ngIf="adMode === 'upload'">
              <button mat-stroked-button type="button" (click)="adFileInput.click()">
                <mat-icon>{{ adMediaType === 'video' ? 'movie' : 'image' }}</mat-icon>
                Choose {{ adMediaType === 'video' ? 'video' : 'image' }}
              </button>
              <span class="ad-file-name">{{ selectedAdFileName || 'No file selected' }}</span>
              <input #adFileInput type="file" [accept]="adMediaType === 'video' ? 'video/*' : 'image/*'" hidden (change)="onAdFileSelected($event)">
            </div>

            <mat-form-field class="ad-input" appearance="outline" *ngIf="adMode === 'url'">
              <mat-label>{{ adMediaType === 'video' ? 'Video URL' : 'Image URL' }}</mat-label>
              <input matInput [(ngModel)]="adImageUrl" placeholder="https://...">
            </mat-form-field>

            <div class="ad-file-picker" *ngIf="adMediaType === 'video' && adMode === 'upload'">
              <button mat-stroked-button type="button" (click)="adThumbnailInput.click()">
                <mat-icon>image</mat-icon>
                Choose thumbnail
              </button>
              <span class="ad-file-name">{{ selectedAdThumbnailFileName || 'No thumbnail selected' }}</span>
              <input #adThumbnailInput type="file" accept="image/*" hidden (change)="onAdThumbnailSelected($event)">
            </div>

            <mat-form-field class="ad-input" appearance="outline" *ngIf="adMediaType === 'video' && adMode === 'url'">
              <mat-label>Thumbnail image URL</mat-label>
              <input matInput [(ngModel)]="adThumbnailUrl" placeholder="https://...">
            </mat-form-field>

            <mat-form-field class="ad-input" appearance="outline" *ngIf="adMediaType !== 'video'">
              <mat-label>Link URL</mat-label>
              <input matInput [(ngModel)]="adLink" placeholder="https://www.teradata.com/...">
            </mat-form-field>

            <button mat-flat-button color="primary" type="button" class="ad-submit-btn"
              (click)="submitAd()" [disabled]="isActionBusy(adSubmitActionKey())">
              <mat-spinner *ngIf="isActionBusy(adSubmitActionKey())" diameter="16" class="btn-spinner"></mat-spinner>
              <mat-icon *ngIf="!isActionBusy(adSubmitActionKey())">add</mat-icon>
              Add ad
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
